import React from 'react';
import './speaker-menu.css';
import { orderBy } from 'lodash';

class SpeakerMenu extends React.Component {
  constructor(props) {
    super(props);

    this.onWheel = this.onWheel.bind(this);
    this.onSearchChange = this.onSearchChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.listRef = React.createRef();

    this.state = {
      display: false,
      x: 0,
      y: 0,
      selectedSpeaker: null,
      wordIndex: null,
      searchText: '',
      highlightedIndex: -1,
    };
  }

  // ***************************************************************************************
  // openMenu()
  // Ouvre le menu de sélection du speaker
  // ***************************************************************************************
  openMenu(x, y, currentSpeakerId, wordIndex) {
    this.setState({
      display: true,
      x,
      y,
      selectedSpeaker: currentSpeakerId,
      wordIndex,
    });
  }

  // ***************************************************************************************
  // closeMenu()
  // Ferme le menu de sélection du speaker
  // ***************************************************************************************
  closeMenu() {
    this.setState({
      display: false,
      x: 0,
      y: 0,
      selectedSpeaker: null,
      wordIndex: null,
      searchText: '',
      highlightedIndex: -1,
    });
  }

  // ***************************************************************************************
  // onSearchChange()
  // Appelé quand le texte de recherche change
  // ***************************************************************************************
  onSearchChange(event) {
    this.setState({
      searchText: event.target.value,
      highlightedIndex: -1,
    });
  }

  // ***************************************************************************************
  // scrollToHighlighted()
  // Scroll l'élément en surbrillance dans la vue
  // ***************************************************************************************
  scrollToHighlighted() {
    if (this.listRef.current && this.state.highlightedIndex >= 0) {
      const items = this.listRef.current.querySelectorAll('.speaker-menu-item');
      const highlightedItem = items[this.state.highlightedIndex];

      if (highlightedItem) {
        highlightedItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.highlightedIndex !== this.state.highlightedIndex) {
      this.scrollToHighlighted();
    }
  }

  onKeyDown(event) {
    const { key } = event;

    // Obtenir les speakers filtrés
    let speakers = orderBy(this.props.speakers, (x) => x.name);
    if (this.state.searchText) {
      const searchLower = this.state.searchText.toLowerCase();
      speakers = speakers.filter((speaker) =>
        speaker.name.toLowerCase().includes(searchLower)
      );
    }

    switch (key) {
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.setState((prevState) => ({
          highlightedIndex: Math.min(
            prevState.highlightedIndex + 1,
            speakers.length - 1
          ),
        }));
        break;

      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.setState((prevState) => ({
          highlightedIndex: Math.max(prevState.highlightedIndex - 1, -1),
        }));
        break;

      case 'Enter':
        event.preventDefault();
        event.stopPropagation();
        if (
          this.state.highlightedIndex >= 0 &&
          speakers[this.state.highlightedIndex]
        ) {
          this.onSelectSpeaker(speakers[this.state.highlightedIndex].id);
        }
        break;

      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.props.onClose();
        break;

      default:
        // Laisser passer les touches de texte
        if (key.length === 1 || key === 'Backspace' || key === 'Delete') {
          event.stopPropagation();
        } else {
          event.preventDefault();
          event.stopPropagation();
        }
    }
  }

  // ***************************************************************************************
  // onSelectSpeaker()
  // Appelé quand l'utilisateur sélectionne un speaker
  // ***************************************************************************************
  onSelectSpeaker(speakerId) {
    if (this.props.onSelectSpeaker) {
      this.props.onSelectSpeaker(speakerId, this.state.wordIndex);
    }
  }

  // ***************************************************************************************
  // onWheel()
  // Appelé quand on scroll dans le menu avec la molette
  // ***************************************************************************************
  onWheel(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  render() {
    if (!this.state.display || !this.props.speakers) {
      return <></>;
    }

    let speakers = orderBy(this.props.speakers, (x) => x.name);

    // Filtrer les speakers en fonction du texte de recherche
    if (this.state.searchText) {
      const searchLower = this.state.searchText.toLowerCase();
      speakers = speakers.filter((speaker) =>
        speaker.name.toLowerCase().includes(searchLower)
      );
    }

    return (
      <div className="speaker-menu-overlay" onClick={this.props.onClose}>
        <div
          className="speaker-menu"
          style={{
            left: `${this.state.x}px`,
            top: `${this.state.y}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="speaker-menu-title">
            {this.props.lang === 'fr'
              ? 'Sélectionner un speaker'
              : 'Select a speaker'}
          </p>
          <label htmlFor="txtSearchSpeaker" className="sr-only">
            {this.props.lang === 'fr'
              ? 'Rechercher un speaker'
              : 'Search a speaker'}
          </label>
          <input
            id="txtSearchSpeaker"
            type="text"
            className="speaker-menu-search"
            placeholder={
              this.props.lang === 'fr' ? 'Rechercher...' : 'Search...'
            }
            value={this.state.searchText}
            onKeyDown={this.onKeyDown}
            onChange={this.onSearchChange}
            autoFocus
          />
          <div
            className="speaker-menu-list"
            ref={this.listRef}
            onWheel={this.onWheel}
            role="list"
          >
            {speakers.map((speaker, index) => {
              const names = [];

              if (speaker.firstname) {
                names.push(speaker.firstname);
              }

              if (speaker.name) {
                names.push(speaker.name);
              }

              return (
                <div
                  role="listitem"
                  key={speaker.id}
                  className={`speaker-menu-item ${
                    speaker.id === this.state.selectedSpeaker ? 'selected' : ''
                  } ${
                    index === this.state.highlightedIndex ? 'highlighted' : ''
                  }`}
                  onClick={() => this.onSelectSpeaker(speaker.id)}
                >
                  <span className="speaker-name">{names.join(' ')}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
}

export default SpeakerMenu;
